import { Fragment, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Eye, EyeOff, RotateCcw, Search, Trash2 } from "lucide-react";
import BONavigation from "components/admin/BONavigation";
import { Badge } from "components/ui/Badge";
import { Button } from "components/ui/Button";
import { Card, StatCard } from "components/ui/Card";
import {
    CacheEntry,
    CacheEntryPreview,
    CacheListResponse,
    deleteCacheEntry,
    getCacheEntries,
    getCacheEntry,
    purgeCache,
} from "interfaces/cacheAdmin";

const PAGE_SIZE = 25;

function formatBytes(n: number): string {
    if (n < 1024) return `${n} o`;
    if (n < 1024 * 1024) return `${(n / 1024).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} Ko`;
    return `${(n / 1024 / 1024).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} Mo`;
}

function formatRelative(iso?: string): string {
    if (!iso) return "—";
    const diff = new Date(iso).getTime() - Date.now();
    const abs = Math.abs(diff);
    const unit = abs < 3600000 ? [Math.round(abs / 60000), "min"] : abs < 86400000 ? [Math.round(abs / 3600000), "h"] : [Math.round(abs / 86400000), "j"];
    return diff >= 0 ? `dans ${unit[0]} ${unit[1]}` : `il y a ${unit[0]} ${unit[1]}`;
}

function pageWindow(page: number, total: number): (number | null)[] {
    const pages = new Set([1, total, page - 1, page, page + 1].filter(p => p >= 1 && p <= total));
    const sorted = [...pages].sort((a, b) => a - b);
    return sorted.flatMap((p, i) => (i > 0 && p - sorted[i - 1] > 1 ? [null, p] : [p]));
}

export default function CacheAdmin() {
    const [data, setData] = useState<CacheListResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [q, setQ] = useState("");
    const [prefix, setPrefix] = useState<string | null>(null);
    const [sort, setSort] = useState<"recent" | "size">("recent");
    const [openId, setOpenId] = useState<string | null>(null);
    const [preview, setPreview] = useState<CacheEntryPreview | null>(null);
    const [busy, setBusy] = useState(false);
    const requestRef = useRef(0);

    // Debounce the search box; any filter change goes back to page 1.
    useEffect(() => {
        const t = setTimeout(() => { setQ(search.trim()); setPage(1); }, 300);
        return () => clearTimeout(t);
    }, [search]);

    const load = async () => {
        const seq = ++requestRef.current;
        setLoading(true);
        try {
            const res = await getCacheEntries({ page, limit: PAGE_SIZE, q: q || undefined, prefix: prefix || undefined, sort });
            if (seq !== requestRef.current) return;
            setData(res);
            setError(null);
            // A purge can leave us past the last page.
            if (page > res.totalPages) setPage(res.totalPages);
        } catch {
            if (seq === requestRef.current) setError("Impossible de charger le cache.");
        } finally {
            if (seq === requestRef.current) setLoading(false);
        }
    };

    useEffect(() => { load(); }, [page, q, prefix, sort]); // eslint-disable-line react-hooks/exhaustive-deps

    const togglePreview = async (entry: CacheEntry) => {
        if (openId === entry._id) { setOpenId(null); setPreview(null); return; }
        setOpenId(entry._id);
        setPreview(null);
        try { setPreview(await getCacheEntry(entry._id)); } catch { setOpenId(null); }
    };

    const removeOne = async (entry: CacheEntry) => {
        if (!window.confirm(`Purger « ${entry.key} » ?`)) return;
        setBusy(true);
        try {
            await deleteCacheEntry(entry._id);
            if (openId === entry._id) { setOpenId(null); setPreview(null); }
            await load();
        } finally {
            setBusy(false);
        }
    };

    const filtered = !!(q || prefix);
    const removeAll = async () => {
        if (!data || data.total === 0) return;
        const scope = filtered ? `les ${data.total.toLocaleString("fr-FR")} entrées filtrées` : `tout le cache (${data.total.toLocaleString("fr-FR")} entrées)`;
        if (!window.confirm(`Purger ${scope} ?`)) return;
        setBusy(true);
        try {
            await purgeCache({ q: q || undefined, prefix: prefix || undefined });
            setOpenId(null);
            setPreview(null);
            setPage(1);
            await load();
        } finally {
            setBusy(false);
        }
    };

    const allCount = data?.prefixes.reduce((s, p) => s + p.count, 0) ?? 0;
    const allSize = data?.prefixes.reduce((s, p) => s + p.size, 0) ?? 0;

    return (
        <div className="min-h-screen bg-canvas">
            <div className="mx-auto max-w-6xl px-4 py-8 md:px-8">
                <BONavigation />

                <div className="mb-6 mt-2 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight text-ink">Cache</h1>
                        <p className="mt-1 text-sm text-ink-muted">
                            Résultats mis en cache dans MongoDB (compteurs, tris, statistiques). Une entrée purgée est recalculée à la prochaine requête.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
                            <RotateCcw className="h-4 w-4" />
                            Actualiser
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={removeAll}
                            disabled={busy || !data || data.total === 0}
                            className="text-danger hover:bg-danger-subtle"
                        >
                            <Trash2 className="h-4 w-4" />
                            {filtered ? "Purger la sélection" : "Tout purger"}
                        </Button>
                    </div>
                </div>

                <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <StatCard label="Entrées" value={allCount.toLocaleString("fr-FR")} />
                    <StatCard label="Taille totale stockée" value={formatBytes(allSize)} />
                    <StatCard label="Sur disque (compressé)" value={data?.storage ? formatBytes(data.storage.storageSize) : "—"} />
                    <StatCard label="Index" value={data?.storage ? formatBytes(data.storage.indexSize) : "—"} />
                </div>

                <Card className="mb-4 space-y-4 p-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative min-w-0 flex-1">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
                            <input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Rechercher une clé"
                                className="h-9 w-full rounded-lg border border-border bg-surface pl-9 pr-3 text-sm text-ink
                                    placeholder:text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            />
                        </div>
                        <div className="inline-flex rounded-full bg-surface-sunken p-1">
                            {(["recent", "size"] as const).map(s => (
                                <button
                                    key={s}
                                    onClick={() => { setSort(s); setPage(1); }}
                                    aria-pressed={sort === s}
                                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors
                                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent
                                        ${sort === s ? "bg-surface text-ink shadow-card" : "text-ink-muted hover:text-ink"}`}
                                >
                                    {s === "recent" ? "Plus récentes" : "Plus lourdes"}
                                </button>
                            ))}
                        </div>
                    </div>

                    {!!data?.prefixes.length && (
                        <div className="flex flex-wrap gap-2">
                            {[{ prefix: null as string | null, count: allCount, size: allSize }, ...data.prefixes].map(p => {
                                const active = prefix === p.prefix;
                                return (
                                    <button
                                        key={p.prefix ?? "__all"}
                                        onClick={() => { setPrefix(p.prefix); setPage(1); }}
                                        aria-pressed={active}
                                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors
                                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent
                                            ${active ? "border-ink bg-ink text-ink-inverse" : "border-border bg-surface text-ink hover:border-border-strong"}`}
                                    >
                                        <span className={p.prefix ? "font-mono" : ""}>{p.prefix ?? "Tous"}</span>
                                        <span className={active ? "text-ink-inverse-muted" : "text-ink-faint"}>
                                            {p.count.toLocaleString("fr-FR")} · {formatBytes(p.size)}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </Card>

                <div className="overflow-hidden rounded-2xl border border-border bg-surface">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-surface-sunken text-xs font-semibold uppercase tracking-wide text-ink-faint">
                                <tr>
                                    <th className="px-4 py-3">Clé</th>
                                    <th className="px-4 py-3 text-right">Taille</th>
                                    <th className="px-4 py-3">Expiration</th>
                                    <th className="px-4 py-3">Mise à jour</th>
                                    <th className="px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {error ? (
                                    <tr><td colSpan={5} className="px-4 py-16 text-center text-danger">{error}</td></tr>
                                ) : loading && !data ? (
                                    <tr><td colSpan={5} className="px-4 py-16 text-center text-ink-faint">Chargement…</td></tr>
                                ) : !data || data.items.length === 0 ? (
                                    <tr><td colSpan={5} className="px-4 py-16 text-center text-ink-muted">Aucune entrée en cache.</td></tr>
                                ) : data.items.map(entry => (
                                    <Fragment key={entry._id}>
                                        <tr className={`border-t border-border transition-colors hover:bg-surface-sunken/60 ${loading ? "opacity-60" : ""}`}>
                                            <td className="max-w-md px-4 py-3">
                                                <p className="truncate font-mono text-xs text-ink" title={entry.key}>{entry.key}</p>
                                            </td>
                                            <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-xs text-ink tabular-nums">
                                                {formatBytes(entry.size)}
                                            </td>
                                            <td className="whitespace-nowrap px-4 py-3 text-xs">
                                                {entry.expired ? (
                                                    <Badge tone="warning">Expirée</Badge>
                                                ) : entry.expiresAt ? (
                                                    <span className="text-ink-muted">{formatRelative(entry.expiresAt)}</span>
                                                ) : (
                                                    <span className="text-ink-faint">Sans TTL</span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-4 py-3 text-xs text-ink-muted">{formatRelative(entry.updatedAt)}</td>
                                            <td className="whitespace-nowrap px-4 py-3 text-right">
                                                <button
                                                    onClick={() => togglePreview(entry)}
                                                    aria-label={openId === entry._id ? "Masquer la valeur" : "Voir la valeur"}
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink
                                                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                                >
                                                    {openId === entry._id ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                </button>
                                                <button
                                                    onClick={() => removeOne(entry)}
                                                    disabled={busy}
                                                    aria-label="Purger cette entrée"
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-danger-subtle hover:text-danger
                                                        disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </td>
                                        </tr>
                                        {openId === entry._id && (
                                            <tr className="border-t border-border bg-surface-sunken/40">
                                                <td colSpan={5} className="px-4 py-3">
                                                    {!preview ? (
                                                        <p className="text-xs text-ink-faint">Chargement…</p>
                                                    ) : (
                                                        <>
                                                            <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-border bg-surface p-3 font-mono text-xs text-ink">
                                                                {preview.preview}
                                                            </pre>
                                                            {preview.truncated && (
                                                                <p className="mt-2 text-xs text-ink-faint">
                                                                    Aperçu tronqué — {preview.length.toLocaleString("fr-FR")} caractères au total.
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {data && data.total > 0 && (
                        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-sm">
                            <p className="text-ink-muted">
                                {data.total.toLocaleString("fr-FR")} entrée{data.total > 1 ? "s" : ""}
                                {filtered && <> · {formatBytes(data.totalSize)}</>}
                            </p>
                            {data.totalPages > 1 && (
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page <= 1}
                                        aria-label="Page précédente"
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-ink transition-colors hover:bg-surface-sunken
                                            disabled:pointer-events-none disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </button>
                                    {pageWindow(page, data.totalPages).map((p, i) =>
                                        p === null ? (
                                            <span key={`gap-${i}`} className="w-6 text-center text-ink-faint">…</span>
                                        ) : (
                                            <button
                                                key={p}
                                                onClick={() => setPage(p)}
                                                aria-current={p === page ? "page" : undefined}
                                                className={`h-8 min-w-[2rem] rounded-lg px-2 text-sm font-medium tabular-nums transition-colors
                                                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent
                                                    ${p === page ? "bg-ink text-ink-inverse" : "text-ink hover:bg-surface-sunken"}`}
                                            >
                                                {p}
                                            </button>
                                        )
                                    )}
                                    <button
                                        onClick={() => setPage(p => Math.min(data.totalPages, p + 1))}
                                        disabled={page >= data.totalPages}
                                        aria-label="Page suivante"
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-ink transition-colors hover:bg-surface-sunken
                                            disabled:pointer-events-none disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
